import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Globe } from "lucide-react";

// Sprint WEB.1 — Impostazioni azienda: canali di pubblicazione.
// Per ogni canale: attivo sì/no + numero massimo foto ("tutte" = vuoto/null).
export default function CanaliPubblicazioneSection({ companyId }) {
  const [canali, setCanali] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/canali-pubblicazione`)
      .then((r) => setCanali(r.data?.canali || []))
      .catch(() => toast.error("Errore caricamento canali"));
  }, [companyId]);

  const setRow = (canale, patch) =>
    setCanali((rows) => rows.map((r) => (r.canale === canale ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        canali: canali.map((c) => ({
          canale: c.canale,
          attivo: !!c.attivo,
          max_foto: c.max_foto === "" || c.max_foto == null ? null : Number(c.max_foto),
        })),
      };
      const r = await api.put(`/companies/${companyId}/canali-pubblicazione`, payload);
      setCanali(r.data?.canali || []);
      toast.success("Canali salvati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-6 border border-slate-200 shadow-sm bg-white" data-testid="canali-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">Canali di pubblicazione</h3>
          <p className="text-sm text-slate-500">Attiva i canali e definisci il numero massimo di foto da inviare a ciascuno.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="canali-save-btn">
          {saving ? "Salvataggio…" : "Salva"}
        </Button>
      </div>

      <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <div className="col-span-6">Canale</div>
          <div className="col-span-3 text-center">Attivo</div>
          <div className="col-span-3">Max foto</div>
        </div>
        {canali.map((c) => (
          <div key={c.canale} className="grid grid-cols-12 gap-3 px-4 py-3 items-center" data-testid={`canali-row-${c.canale}`}>
            <div className="col-span-6 flex items-center gap-2">
              <Globe className="h-4 w-4 text-slate-400" />
              <span className="font-medium text-sm text-slate-800">{c.label}</span>
              {!c.is_portale && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">SITO</span>}
            </div>
            <div className="col-span-3 flex justify-center">
              <Checkbox checked={!!c.attivo} onCheckedChange={(v) => setRow(c.canale, { attivo: v === true })}
                        data-testid={`canali-attivo-${c.canale}`} />
            </div>
            <div className="col-span-3">
              <Input
                type="number" min="0"
                value={c.max_foto == null ? "" : c.max_foto}
                onChange={(e) => setRow(c.canale, { max_foto: e.target.value })}
                placeholder="Tutte"
                data-testid={`canali-maxfoto-${c.canale}`}
              />
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-3">Lascia "Max foto" vuoto per inviare <strong>tutte</strong> le foto (nessun limite).</p>
    </Card>
  );
}
